import React, { useEffect, useMemo, useState } from 'react';
import { Contract } from 'cpes-common';
import { Layout } from '@vwfs-bronson/bronson-react';
import { TranslationFormat, useTranslationWithFormatting } from 'localization/useTranslationWithFormatting';
import { DocumentTypeSelect } from './DocumentTypeSelect';
import { TimeframeSelect } from './TimeframeSelect';
import { DocumentsTable } from './DocumentsTable';
import { useServerPaginatedDocuments } from './useServerPaginatedDocuments';
import { usePageResetOnChange } from './usePageResetOnChange';
import { InvoiceDocument } from './types';
import { PostboxDocumentType } from '../helper/DocumentType';
import { getDocumentTypeOptionsForContracts } from './contractDocumentTypes';
import { AllTimeframes, Timeframe, timeframeMomentMapping } from '../helper/Timeframe';

const invoiceDocumentTypeOptions: PostboxDocumentType[] = ['INVOICE', 'LEASING_INVOICE', 'BILLING_REPORT'];
const pageSize = 10;

export const InvoicesTab: React.FC<{
    contracts: Contract[];
    contractNumber: string;
    initialTimeframe?: Timeframe | AllTimeframes;
    isActive?: boolean;
}> = ({ contracts, contractNumber, initialTimeframe = 'LAST_6_MONTHS', isActive = true }) => {
    const { f } = useTranslationWithFormatting('postbox');

    const [page, setPage] = usePageResetOnChange(contractNumber);
    const [documentType, setDocumentType] = useState<PostboxDocumentType>('ALL');
    const [timeframe, setTimeframe] = useState<Timeframe | AllTimeframes>(initialTimeframe);

    const documentTypeOptions = useMemo(
        () => getDocumentTypeOptionsForContracts(invoiceDocumentTypeOptions, contracts, contractNumber),
        [contracts, contractNumber],
    );
    const isDocumentTypeAvailable = documentType === 'ALL' || documentTypeOptions.includes(documentType);

    useEffect(() => {
        if (!isDocumentTypeAvailable) {
            setDocumentType('ALL');
        }
    }, [isDocumentTypeAvailable]);

    const fromDate = useMemo(() => (timeframe === 'ALL' ? undefined : timeframeMomentMapping()[timeframe]), [
        timeframe,
    ]);

    const { data, isLoading, loadingError } = useServerPaginatedDocuments<InvoiceDocument>(
        isActive ? '/invoice-documents' : null,
        {
            page,
            pageSize,
            documentType: documentType === 'ALL' || !isDocumentTypeAvailable ? undefined : documentType,
            contractNumber: contractNumber === 'ALL' ? undefined : contractNumber,
            fromDate,
        },
    );

    const resetToFirstPage = <T,>(setter: (value: T) => void) => (value: T) => {
        setter(value);
        setPage(1);
    };

    return (
        <>
            <Layout>
                <Layout.Item default={'1/4'} xs={'1/1'}>
                    <DocumentTypeSelect
                        options={documentTypeOptions}
                        value={isDocumentTypeAvailable ? documentType : 'ALL'}
                        onChange={resetToFirstPage(setDocumentType)}
                    />
                </Layout.Item>

                <Layout.Item default={'1/4'} xs={'1/1'}>
                    <TimeframeSelect value={timeframe} onChange={resetToFirstPage(setTimeframe)} />
                </Layout.Item>
            </Layout>

            <DocumentsTable
                documents={data?.documents}
                isLoading={isLoading}
                hasError={!!loadingError}
                currentPage={data?.currentPage ?? page}
                totalPages={data?.totalPages ?? 1}
                onPageChange={setPage}
                renderDetails={document => {
                    const invoiceNumber = document.invoiceNumber?.trim();
                    const hasInvoiceAmount = document.invoiceAmount !== undefined && document.invoiceAmount !== null;
                    if (!invoiceNumber && !hasInvoiceAmount) {
                        return document.fileName ? `(${document.fileName})` : null;
                    }
                    return [
                        invoiceNumber,
                        hasInvoiceAmount && `(${f(document.invoiceAmount, TranslationFormat.CURRENCY)})`,
                    ]
                        .filter(Boolean)
                        .join(' ');
                }}
            />
        </>
    );
};
